import { last } from 'lodash';
import { nanoid } from 'nanoid';
import type { Poll } from 'schema/poll';
import { AnswerState, ICState } from 'schema/state';
import { getEngine } from 'shared/src/utils';
import { assign, PromiseActorLogic, setup } from 'xstate';
import { back, change, next, track, TrackActionParams, updateMediaProgress, updateOthers } from './actions';
import { createDefaultState } from './actions/creator';
import {
  markAnswerAnswered,
  markAnswerAutoCommited,
  markAnswerSkipped,
  markAnswerTracked,
  markAnswerValidity,
  markQuestionExpired,
  markUnanswerableValidity,
  reduceFirst,
} from './actions/reducers';
import { validateAnswers, validateAnswersOnChange, validateUnanswerable } from './actions/validate';
import type { ILoginProvider } from './authTypes';
import { pollPreStartTransform } from './external/pollPreStartTransformer';
import { isAuthQuestion } from './guards';
import { assertPollInvariants } from './machine/asserts';
import { OauthUser } from './machine/authState';
import type { InterpreterIntl } from './machine/interpreter';
import { TrackingError } from './machine/pixelSender';
import { IMediaAnalytics, MEDIA_EVENTS } from './mediaAnalyticsTypes';
import {
  isFinalWithZeroAutoClose,
  selectDisplayedOrAllGroupQuestions,
  selectGroup,
  selectQuestion,
  selectRelevantQuestions,
} from './selects';
import type { ITracking } from './trackingTypes';
import {
  findLastInStack,
  findLastInStackSafe,
  findLastStoredQuestionSafe,
  getMapFromStack,
  getOverallResult,
  hasFeedback,
  hasImageFeedback,
  questionHasDisclaimer,
} from './utils';

type ShouldTrackingDependentActionBeResolved = (params: { state: ICState; poll: Poll }) => boolean;

export interface PollMachineContext {
  poll: Poll;
  state: ICState;
  nextState?: ICState;
  /** Состояние восстановленное из хранилища */
  user: OauthUser | undefined;
  nextCalculatorError: Error | undefined;
  trackingError: TrackingError | undefined;
  initServerPassPollError: Error | undefined;
  retryCount: number;
  /** Указывает, начал ли взаимодействие с машиной пользователь */
  hasStarted: boolean;
  /** Список отложенных событий, которые должны быть выполнены по условию при обновлении трекинга */
  trackingDependentActionList: {
    action: Omit<ExternalAction, 'state'>;
    shouldBeResolved: ShouldTrackingDependentActionBeResolved;
  }[];
}
export type PollMachineEvents =
  | {
      type: 'change';
      questionId: string;
      answeredIds: string[];
      answersOrder?: number[];
      touchedBy?: NonNullable<AnswerState['lastTouchedBy']>;
    }
  | {
      type: 'continueAuthenticated';
    }
  | {
      type: 'skipAuthentication';
    }
  | {
      type: 'startAuthentication';
    }
  | {
      type: 'refreshAuthentication';
    }
  | {
      type: 'othersChange';
      questionId: string;
      value: string;
      touchedBy?: NonNullable<AnswerState['lastTouchedBy']>;
    }
  | {
      type: 'failedChangeValidation';
    }
  | {
      type: 'successfulChangeValidation';
    }
  | {
      type: 'answer';
    }
  | {
      type: 'tryAnswer';
    }
  | {
      type: 'back';
    }
  | {
      type: 'skip';
    }
  | {
      type: 'globalTimerStart';
    }
  | {
      type: 'fileUpload';
      questionId: string;
      fileName: string;
      path: string | null;
    }
  | {
      type: 'welcomeClick';
    }
  | {
      type: 'retryLoadingAuthState';
    }
  | ({
      type: 'trackStatusUpdate';
    } & TrackActionParams)
  | {
      type: 'mediaProgress';
      mediaProgress: 'start' | 'firstQuartile' | 'midpoint' | 'thirdQuartile' | 'complete';
      questionId: string;
    }
  | {
      type: 'linkClick';
      external_url: string;
    }
  | {
      type: 'invokeDeeplink';
      deeplink: DeeplinkAction['deeplink'];
    }
  | {
      type: 'finalClick';
      finalId: string;
    }
  | { type: 'questionFocus' }
  | ({
      type:
        | 'xstate.error.actor.pixelSender'
        | 'xstate.error.actor.nextCalculator'
        | 'xstate.error.actor.beforeAuthStateRestorer'
        | 'xstate.error.actor.beforeAuthStateDumper'
        | 'xstate.error.actor.authStateLoader'
        | 'xstate.error.actor.initServerPassPoll'
        | 'xstate.error.actor.externalLogicRulesLoader'
        | 'xstate.error.actor.persistentStateRestorer';
    } & (
      | {
          error: Error;
        }
      | Record<string, never>
    ))
  | {
      type: 'xstate.done.actor.externalLogicRulesLoader';
      output: NonNullable<ICState['externalLogicRules']>;
    }
  | {
      type: 'xstate.done.actor.pixelSender';
      output: string | null;
    }
  | {
      type: 'xstate.done.actor.authStateLoader';
      output: OauthUser | undefined;
    }
  | {
      type: 'xstate.done.actor.initServerPassPoll';
      output: {
        poll: Poll;
        questionsIndices?: string[];
        links: ICState['links'];
        externalLogicRules: ICState['externalLogicRules'];
      };
    }
  | {
      type: 'xstate.done.actor.persistentStateRestorer';
      output: ICState | undefined;
    }
  | {
      type: 'xstate.done.actor.beforeAuthStateRestorer';
      output: ICState | undefined;
    }
  | {
      type: 'xstate.done.actor.nextCalculator';
      output: { poll: Poll; state: ICState };
    }
  | {
      type: 'retryPixelSend';
    }
  | {
      type: 'retryNextCalculator';
    }
  | {
      type: 'retryInitServerPassPoll';
    }
  | {
      type: 'next';
    }
  | {
      type: 'close';
    }
  | {
      type: 'mediaProgressChange';
      questionId: string;
      progress: number;
    };

type SimpleExternalAction<T extends string> = {
  state: ICState;
  poll: Poll;
  type: T;
};

type OverallAction = SimpleExternalAction<'overall'> & { overallResult: ReturnType<typeof getOverallResult> };

type ChangeAnswerAction = SimpleExternalAction<'changeAnswer'> & {
  questionId: string;
  answeredIds: string[];
  answersOrder: number[] | undefined;
};

type CloseAction = SimpleExternalAction<'close'> & {
  unsafe: boolean;
  eventId: string;
};

type AutocloseAction = SimpleExternalAction<'autoclose'> & {
  unsafe: boolean;
  eventId: string;
};

type DeeplinkPayload =
  | {
      method: 'complete';
    }
  | {
      method: 'close';
    }
  | {
      method: 'deeplink';
      query: `?deeplink=${string}`;
    };

export type DeeplinkAction = SimpleExternalAction<'deeplink'> & {
  deeplink: {
    by: 'location' | 'heuristic';
    payload: DeeplinkPayload | DeeplinkPayload[];
    onResolve?: VoidFunction;
  };
};

export type ExternalAction =
  | SimpleExternalAction<'start'>
  | SimpleExternalAction<'load'>
  | SimpleExternalAction<'done'>
  | SimpleExternalAction<'done_requests'>
  | SimpleExternalAction<'wrong_configuration'>
  | SimpleExternalAction<'completed'>
  | SimpleExternalAction<'passed'>
  | SimpleExternalAction<'ready'>
  | SimpleExternalAction<'network_error'>
  | SimpleExternalAction<'ready_after_error'>
  | SimpleExternalAction<'clear_token_in_storage'>
  | OverallAction
  | DeeplinkAction
  | ChangeAnswerAction
  | CloseAction
  | AutocloseAction;

export type InvokeAction = (action: ExternalAction) => void;

export type PollMachine = ReturnType<typeof createPollMachine>;
export type SendError = (
  error: Error,
  options?: {
    state?: ICState;
    attachAuth?: boolean;
    answerState?: AnswerState;
    severity?: 'MINOR' | 'MAJOR' | 'CRITICAL';
    actualError?: unknown;
    additionalInfo?: Record<string, string | number | undefined | null>;
  },
) => void;

export type SendNextStep = (data: { poll: Poll; state: ICState }) => Promise<{
  patch: {
    questionDecks: Poll['questionDecks'];
    questions: Poll['questions'];
    finals: Poll['finals'];
    groups: Poll['groups'];
    sets: Poll['sets'];
  };
  state: ICState;
}>;

export type SendInitPoll = (data: { poll: Poll; state: ICState }) => Promise<{
  questionsIndices?: string[];
  links: ICState['links'];
  externalLogicRules?: ICState['externalLogicRules'];
  poll: Poll;
}>;

export function createPollMachine({
  poll: originalPoll,
  tracking,
  OAuth,
  intl,
  sendError,
  isDebugMode,
  invokeAction,
  mediaAnalitics: { resolveAllPendingEvents, hasPendingEvents },
  externalLogicRules,
  preinitPollsIc,
}: {
  poll: Poll;
  tracking: Pick<
    ITracking,
    | 'load'
    | 'setUser'
    | 'setPoll'
    | 'welcomeClick'
    | 'upload'
    | 'finalClick'
    | 'trackMediaProgress'
    | 'isStarted'
    | 'init'
    | 'linkClick'
  >;
  OAuth: Pick<ILoginProvider, 'fetchAuthorizationCode' | 'getAccessToken'>;
  strict?: boolean;
  intl: InterpreterIntl;
  isDebugMode?: boolean;
  sendError: SendError;
  invokeAction: InvokeAction;
  mediaAnalitics: IMediaAnalytics;
  externalLogicRules: ICState['externalLogicRules'] | undefined;
  preinitPollsIc?: string | null;
}) {
  assertPollInvariants(originalPoll);
  const defaultState = createDefaultState(preinitPollsIc);
  return setup({
    types: {} as {
      context: PollMachineContext;
      events: PollMachineEvents;
    },
    actors: {} as {
      authStateLoader: PromiseActorLogic<PollMachineContext['user'], PollMachineContext, any>;
      initServerPoll: PromiseActorLogic<unknown, PollMachineContext, any>;
      befareAuthStateRestorer: PromiseActorLogic<unknown, PollMachineContext, any>;
      nextCalculator: PromiseActorLogic<Pick<PollMachineContext, 'poll' | 'state'>, PollMachineContext, any>;
      pixelSender: PromiseActorLogic<string | null, PollMachineContext, any>;
      beforeAuthStateDumper: PromiseActorLogic<void, PollMachineContext, any>;
      initServerPassPoll: PromiseActorLogic<Partial<ICState>, PollMachineContext, any>;
      externalLogicRulesLoader: PromiseActorLogic<unknown, PollMachineContext, any>;
      persistentStateRestorer: PromiseActorLogic<ICState | undefined, PollMachineContext, any>;
      beforeAuthStateRestorer: PromiseActorLogic<ICState | undefined, PollMachineContext, any>;
      immediatelyUpdateStorageState: PromiseActorLogic<unknown, PollMachineContext, any>;
    },
    actions: {
      doSetExternalLogicRules: assign(({ context: { state }, event }) => {
        if (event.type === 'xstate.done.actor.externalLogicRulesLoader') {
          return {
            state: {
              ...state,
              externalLogicRules: event.output,
            },
          };
        }
        return {};
      }),
      doSetLastPixelHash: assign(({ context: { state }, event }) => {
        if (event.type === 'xstate.done.actor.pixelSender') {
          return {
            state: {
              ...state,
              lastPixelHash: event.output,
            },
          };
        }
        return {};
      }),
      doLogExternalLogicRulesError: ({ context: { state }, event }) => {
        console.error('Happened');
        if (event.type === 'xstate.error.actor.externalLogicRulesLoader') {
          sendError(new Error('externalLogicRulesLoader service invocation error'), {
            state,
            severity: 'MAJOR',
            actualError: event.error,
          });
        } else {
          sendError(new Error('Unknown event was sent to doLogExternalLogicRulesError: ' + event.type), { state });
        }
      },
      doLogAuthErrorAfterRedirect: ({ context: { state } }) => {
        sendError(new Error('Unauthorized after redirect'), { state, attachAuth: true, severity: 'MAJOR' });
      },
      doLogStorageError: ({ context: { state }, event }) => {
        if (
          event.type === 'xstate.error.actor.persistentStateRestorer' ||
          event.type === 'xstate.error.actor.beforeAuthStateRestorer' ||
          event.type === 'xstate.error.actor.beforeAuthStateDumper' ||
          event.type === 'xstate.error.actor.authStateLoader'
        ) {
          sendError(new Error('authStateLoader service invocation error'), {
            state,
            attachAuth: true,
            actualError: event.error,
          });
        } else {
          sendError(new Error('Unknown event was sent to doLogStorageError: ' + event.type), {
            state,
            attachAuth: true,
          });
        }
      },
      doRedirectToLogin: () => {
        const LOGIN_DELAY = 100; // Задержка для отправки quiz-start
        setTimeout(() => {
          OAuth.fetchAuthorizationCode();
        }, LOGIN_DELAY);
      },
      doChangeToAuthorizedAnswer: assign(({ context: { poll, state } }) => {
        if (!state.questionId) return {};
        const authQuestion = selectQuestion(poll, state.questionId);
        if (authQuestion.data.type !== 'POLL_AUTH') return {};
        const [yesAnswer] = authQuestion.data.answers;
        if (!yesAnswer) return {};

        return {
          state: change(
            poll,
            state,
            {
              questionId: authQuestion.id,
              answeredIds: [yesAnswer.id],
              touchedBy: 'user',
            },
            intl,
          ),
        };
      }),
      doChangeToUnauthorizedAnswer: assign(({ context: { poll, state } }) => {
        if (!state.questionId) return {};
        const authQuestion = selectQuestion(poll, state.questionId);
        if (authQuestion.data.type !== 'POLL_AUTH') return {};
        const [, noAnswer] = authQuestion.data.answers;
        if (!noAnswer) return {};

        return {
          state: change(
            poll,
            state,
            {
              questionId: authQuestion.id,
              answeredIds: [noAnswer.id],
              touchedBy: 'user',
            },
            intl,
          ),
        };
      }),
      doSetTrackingUser: ({ context: { user } }) => {
        tracking.setUser(user ?? null);
      },
      doSetTrackingUserIfHadAnsweredAuthSuccessfully: ({ context: { poll, state, user } }) => {
        const authQuestion = poll.questions.find(isAuthQuestion);
        if (authQuestion) {
          const answerState = findLastInStackSafe(state.answersStack, authQuestion.id);
          if (answerState) {
            const yesAnswerId = authQuestion.data.answers[0]?.id;
            const choosedYesAnswer = !!answerState.answeredIds?.find(id => id === yesAnswerId);
            if (answerState.isAnswered && choosedYesAnswer) {
              if (!user?.id) {
                sendError(new Error('User had answered auth question and is not authenticated!'), {
                  state,
                  attachAuth: true,
                  severity: 'MAJOR',
                });
              } else {
                tracking.setUser(user);
              }
            }
          } else if (user?.id) {
            console.warn("User haven't answered auth question but is authenticated");
          }
        } else if (user?.id) {
          console.warn("Poll doesn't have auth question but user is authenticated");
        }
      },
      doBack: assign(({ context: { state, poll } }) => ({
        state: back(poll, state),
      })),
      doMarkTimeExpired: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markQuestionExpired, state),
      })),
      doChange: assign(({ context: { state, poll }, event }) => {
        if (event.type !== 'change') {
          return {};
        }

        const { questionId, answeredIds, answersOrder, touchedBy = 'user' } = event;

        if (poll.provideParentOverall) {
          invokeAction({
            type: 'changeAnswer',
            state,
            poll,
            questionId,
            answeredIds,
            answersOrder,
          });
        }

        return {
          state: change(
            poll,
            state,
            {
              questionId,
              answeredIds,
              answersOrder,
              touchedBy,
            },
            intl,
          ),
        };
      }),
      doFileUpload: assign(({ context: { state, poll }, event }) => {
        return {
          state:
            event.type !== 'fileUpload'
              ? state
              : updateOthers(
                  poll,
                  state,
                  {
                    questionId: event.questionId,
                    value: event.path ?? '',
                    touchedBy: 'user',
                  },
                  intl,
                ),
        };
      }),
      doTrackFileUpload: ({ event }) => {
        if (event.type === 'fileUpload') {
          tracking.upload({ fileName: event.fileName, path: event.path, questionId: event.questionId });
        }
      },
      doOthersChange: assign(({ context: { state, poll }, event }) => {
        return {
          state:
            event.type !== 'othersChange'
              ? state
              : updateOthers(
                  poll,
                  state,
                  {
                    questionId: event.questionId,
                    value: event.value,
                    touchedBy: event.touchedBy ?? 'user',
                  },
                  intl,
                ),
        };
      }),
      doSetNextState: assign(({ event }) => {
        if (event.type === 'xstate.done.actor.nextCalculator') {
          tracking.setPoll(event.output.poll);
          return { poll: event.output.poll, nextState: event.output.state };
        }
        return {};
      }),
      doTrackStatusUpdate: assign(({ context: { state, poll, trackingDependentActionList }, event }) => {
        if (event.type !== 'trackStatusUpdate') {
          return {};
        }

        const nextState = track(poll, state, {
          trackingState: event.trackingState,
          tracking: event.tracking,
        });

        const remainingActions: PollMachineContext['trackingDependentActionList'] = [];
        for (const action of trackingDependentActionList) {
          if (action.shouldBeResolved({ state: nextState, poll })) {
            invokeAction({ ...action.action, state: nextState } as ExternalAction);
          } else {
            remainingActions.push(action);
          }
        }

        return {
          state: nextState,
          trackingDependentActionList: remainingActions,
        };
      }),
      doTrackLinkClick: ({ event }) => {
        if (event.type === 'linkClick') {
          tracking.linkClick({ external_url: event.external_url });
        }
      },
      doTrackMediaProgress: ({ event }) => {
        if (event.type === 'mediaProgress') {
          tracking.trackMediaProgress({ media_progress: event.mediaProgress, questionId: event.questionId });
        }
      },
      doTrackFinalClick: ({ event }) => {
        if (event.type === 'finalClick') {
          tracking.finalClick({ finalId: event.finalId });
        }
      },
      doClearTrackingErrors: assign(({ context: { trackingError, state, poll } }) => {
        if (trackingError) {
          invokeAction({ type: 'ready_after_error', state, poll });
        }

        return {
          trackingError: undefined,
          retryCount: 0,
        };
      }),
      doClearNextCalculatorErrors: assign(({ context: { nextCalculatorError, state, poll } }) => {
        if (nextCalculatorError) {
          invokeAction({ type: 'ready_after_error', state, poll });
        }

        return {
          nextCalculatorError: undefined,
          retryCount: 0,
        };
      }),
      doAddNextCalculatorErrorAndIncrementRetryCount: assign(
        ({ context: { state, retryCount, trackingError: previousError }, event }) => {
          if (event.type === 'xstate.error.actor.nextCalculator') {
            sendError(new Error('nextCalculator service invocation error'), {
              state,
              severity: 'MAJOR',
              actualError: event.error,
            });
            return { nextCalculatorError: event.error, retryCount: previousError ? retryCount + 1 : 0 };
          }
          return {};
        },
      ),
      doAddTrackingErrorAndIncrementRetryCount: assign(
        ({ context: { state, retryCount, trackingError: previousError }, event }) => {
          if (event.type === 'xstate.error.actor.pixelSender') {
            const trackingError =
              event.error instanceof TrackingError
                ? event.error
                : new TrackingError(state.questionId, state.groupId, state.lastPixelHash ?? null);
            sendError(trackingError, { state });

            return { trackingError, retryCount: previousError ? retryCount + 1 : 0 };
          }

          return {};
        },
      ),
      doinitServerPassPollErrorAndIncrementRetryCount: assign(
        ({ context: { state, retryCount, trackingError: previousError }, event }) => {
          if (event.type === 'xstate.error.actor.initServerPassPoll') {
            sendError(event.error, { state, severity: 'MAJOR' });
            return { initServerPassPollError: event.error, retryCount: previousError ? retryCount + 1 : 0 };
          }
          return {};
        },
      ),
      doClearinitServerPassPollErrors: assign(({ context: { initServerPassPollError, state, poll } }) => {
        if (initServerPassPollError) {
          invokeAction({ type: 'ready_after_error', state, poll });
        }

        return {
          initServerPassPollError: undefined,
          retryCount: 0,
        };
      }),
      doPreStartTransformPoll: assign(({ context: { state, poll } }) => {
        const {
          links,
          questionsIndices,
          poll: transformedPoll,
        } = pollPreStartTransform({ poll, questionsIndices: state.questionsIndices, links: state.links });
        return {
          poll: transformedPoll,
          state: { ...state, questionsIndices, links },
        };
      }),
      doUseFetchedState: assign(({ context: { state }, event }) =>
        event.type === 'xstate.done.actor.beforeAuthStateRestorer' ||
        event.type === 'xstate.done.actor.persistentStateRestorer'
          ? { state: event.output ? { ...state, ...event.output } : state }
          : {},
      ),
      doInitializeServerPassPoll: assign(({ context: { poll, state }, event }) => {
        if (event.type === 'xstate.done.actor.initServerPassPoll') {
          tracking.setPoll({ ...poll, ...event.output.poll });
          return {
            poll: event.output.poll ? { ...poll, ...event.output.poll } : poll,
            state: {
              ...state,
              links: state.links ?? event.output.links,
              questionsIndices: state.questionsIndices ?? event.output.questionsIndices,
              externalLogicRules: state.externalLogicRules ?? event.output.externalLogicRules,
            },
          };
        }
        return {};
      }),
      doMarkAnswerTrackingStatus: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markAnswerTracked, state),
      })),

      doMarkInvalidAnswers: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll })
          .filter(q => !findLastInStack(state.answersStack, q.id).isWithoutAnswers)
          .reduce((s, q, _, relevantQuestions) => markAnswerValidity(s, q, relevantQuestions, intl), state),
      })),
      doMarkAutoCommited: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markAnswerAutoCommited, state),
      })),
      doMarkAnswered: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markAnswerAnswered, state),
      })),
      doMarkSkipped: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markAnswerSkipped, state),
      })),
      doSetAuthState: assign(({ event }) =>
        event.type === 'xstate.done.actor.authStateLoader' ? { user: event.output } : {},
      ),
      doSetEmptyAuthState: assign(() => ({
        user: undefined,
      })),
      doNotifyDoneRequests: assign(({ context: { state, poll, trackingDependentActionList } }) => {
        const action = { type: 'done_requests', state, poll } as const;

        if (canClosePoll({ state, poll })) {
          invokeAction(action);
          return {};
        }

        return {
          trackingDependentActionList: [...trackingDependentActionList, { action, shouldBeResolved: canClosePoll }],
        };
      }),
      doNotifyDone: assign(({ context: { state, poll, trackingDependentActionList } }) => {
        const action = { type: 'done', state, poll } as const;
        const shouldBeResolved: ShouldTrackingDependentActionBeResolved = params => {
          return canClosePoll({ ...params, waitOnlySystemPixels: true });
        };

        if (shouldBeResolved({ state, poll })) {
          invokeAction(action);
          return {};
        }

        return {
          trackingDependentActionList: [...trackingDependentActionList, { action, shouldBeResolved }],
        };
      }),
      doNotifyWrongConfiguration: ({ context: { state, poll } }) => {
        invokeAction({ type: 'wrong_configuration', state, poll });
      },
      doNotifyCompleted: ({ context: { state, poll } }) => {
        invokeAction({ type: 'completed', state, poll });
      },
      doNotifyPassed: ({ context: { state, poll } }) => {
        invokeAction({ type: 'passed', state, poll });
      },
      doNotifyReady: ({ context: { state, poll } }) => {
        invokeAction({ type: 'ready', state, poll });
      },
      doNotifyNetworkError: ({ context: { state, poll } }) => {
        invokeAction({ type: 'network_error', state, poll });
      },
      doClearTokenInStorage: ({ context: { state, poll } }) => {
        invokeAction({ type: 'clear_token_in_storage', state, poll });
      },
      doClose: assign(({ context: { state, poll, trackingDependentActionList } }) => {
        const action = { type: 'close' as const, state, poll, eventId: nanoid(10), unsafe: false };
        invokeAction({ ...action, unsafe: true });

        if (canClosePoll({ state, poll })) {
          invokeAction(action);
          return {};
        }

        return {
          trackingDependentActionList: [...trackingDependentActionList, { action, shouldBeResolved: canClosePoll }],
        };
      }),
      doAutoClose: assign(({ context: { poll, state, trackingDependentActionList } }) => {
        const getNextState = (state: ICState) => {
          const finalId = state.readOnly ? next(poll, state).finalId : state.finalId;
          return { ...state, finalId };
        };

        const nextState = getNextState(state);
        const action = { type: 'autoclose' as const, state: nextState, poll, eventId: nanoid(10), unsafe: false };
        invokeAction({ ...action, unsafe: true });

        if (canClosePoll({ state: nextState, poll })) {
          invokeAction(action);
          return {};
        }

        return {
          trackingDependentActionList: [
            ...trackingDependentActionList,
            {
              action,
              shouldBeResolved: ({ state, poll }) => {
                return canClosePoll({ state: getNextState(state), poll });
              },
            },
          ],
        };
      }),
      doNotifyOverall: ({ context: { state, poll } }) => {
        if (poll.provideParentOverall && state.finalId) {
          const overallResult = getOverallResult({ state, poll, finalId: state.finalId });
          invokeAction({ type: 'overall', state, poll, overallResult });
        }
      },
      doSetWelcome: assign(({ context: { state, poll } }) => {
        const [welcome] = poll.welcome ?? [];

        if (welcome) return { state: { ...state, welcomeId: welcome.id } };

        throw new Error('Welcome must be defined!');
      }),
      doTrackLoad: ({ context: { state, poll } }) => {
        invokeAction({ type: 'load', state, poll });
        tracking.isStarted = state.startedAt != null;
        if (!tracking.isStarted) tracking.load();
      },
      doUseNextState: assign(({ context: { state, nextState, poll } }) => {
        if (!nextState) throw new Error("Can't find nextState");

        /** Берем answersStack из текущего state и добавляем к нему новый элементы из nextState.answersStack */
        /** Это защищает нас от изменений в state.answersStack после получения nextState */
        const answersStack = [...state.answersStack, ...nextState.answersStack.slice(state.answersStack.length)];
        const trackingStack = state.trackingStack;
        if (nextState?.finalId && isFinalWithZeroAutoClose(poll, nextState.finalId)) {
          return { state: { ...state, readOnly: true }, nextState: undefined };
        }
        return {
          state: { ...nextState, trackingStack, answersStack, lastPixelHash: state.lastPixelHash },
          nextState: undefined,
        };
      }),
      doInit: assign(({ context: { state, poll } }) => ({
        state: reduceFirst(poll, state),
      })),
      doStartGlobalTimer: assign(({ context: { state } }) => ({
        state: { ...state, startedAt: state.startedAt ?? new Date().getTime() },
      })),
      doTrackStart: assign(({ context: { state, hasStarted, poll } }) => {
        if (!hasStarted) {
          tracking.init();
          invokeAction({ type: 'start', state, poll });
        }
        return { hasStarted: true };
      }),
      doWelcomeClick: assign(({ context: { state, poll } }) => ({
        state: reduceFirst(poll, {
          ...state,
          startedAt: new Date().getTime(),
          welcomeId: null,
        }),
      })),
      doTrackWelcomeClick: () => {
        tracking.welcomeClick();
      },
      doSetFinishedAt: assign(({ context: { state } }) => {
        return { state: { ...state, finishedAt: new Date().getTime() } };
      }),
      doTrackImageVisibility: () => {
        // TODO: refactor by moving pending events into contexts
        if (hasPendingEvents()) {
          const pendingEventGroups = resolveAllPendingEvents(MEDIA_EVENTS.COMPLETE);
          for (const { questionId, pendingEvents } of pendingEventGroups) {
            tracking.trackMediaProgress({ media_progress: MEDIA_EVENTS.COMPLETE, questionId, ...pendingEvents });
          }
        }
      },
      doMediaProgressChange: assign(({ context: { state }, event }) => {
        return {
          state:
            event.type !== 'mediaProgressChange' ? state : updateMediaProgress(state, { ...event, touchedBy: 'user' }),
        };
      }),
      doMarkInvalidUnanswerable: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll })
          .filter(q => findLastInStack(state.answersStack, q.id).isWithoutAnswers)
          .reduce((s, q) => markUnanswerableValidity(s, q, intl), state),
      })),
      doInvokeDeeplink: assign(({ context: { poll, state, trackingDependentActionList }, event }) => {
        if (event.type !== 'invokeDeeplink') {
          return {};
        }

        const action = { type: 'deeplink' as const, deeplink: event.deeplink, poll };

        if (canClosePoll({ state, poll })) {
          invokeAction({ ...action, state, poll });
          return {};
        }

        return {
          trackingDependentActionList: [...trackingDependentActionList, { action, shouldBeResolved: canClosePoll }],
        };
      }),
    },
    delays: {
      REMAINED_TIMER: ({ context: { state, poll } }) => {
        const screenTimerInfo = getScreenTimerInfo({ poll, state });

        if (state.startedAt && poll.hasTimer && poll.timerDuration !== undefined) {
          return Math.max(0, poll.timerDuration * 1000 - (Date.now() - state.startedAt));
        } else if (screenTimerInfo?.timerDuration) {
          const timerCreatedAt = getTimerCreatedAt(state, poll) ?? Date.now();

          return Math.max(0, screenTimerInfo.timerDuration * 1000 - (Date.now() - timerCreatedAt));
        }

        const oneYear = 365 * 7 * 24 * 60 * 60 * 1000; // i.e. never

        return oneYear;
      },
      AUTO_CLOSE_TIMEOUT: ({ context: { state, poll } }) => {
        const final = poll.finals.find(final => final.id === state.finalId);
        return final?.data?.hasAutoClose ? final.data.autoCloseMs ?? 0 : 0;
      },
    },
    guards: {
      isReturningFromAuth: ({ context: { poll, state } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        if (question.data.type !== 'POLL_AUTH') return false;

        const authAnswerState = findLastInStack(state.answersStack, state.questionId);
        const haveChangedAuthAnswer = !!authAnswerState.answeredIds?.length;
        return haveChangedAuthAnswer;
      },
      isAuthenticatedWithSkipAuthenticated: ({ context: { state, poll, user } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        if (question.data.type !== 'POLL_AUTH') return false;

        return !!user?.id && !!question.data.skipAuthenticated;
      },
      isNotRequiredAuth: ({ context: { state, poll } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        if (question.data.type !== 'POLL_AUTH') return false;

        return !question.data.requiresAnswer;
      },
      isAuthenticated: ({ context: { user } }) => {
        return !!user?.id;
      },
      isNotAuthenticated: ({ context: { user } }) => {
        return !user?.id;
      },
      isOnAuthStep: ({ context: { poll, state } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        return question.data.type === 'POLL_AUTH';
      },
      isWithAuthStep: ({ context: { poll } }) => {
        return poll.questions.some(q => q.data.type === 'POLL_AUTH');
      },
      isDebugMode: () => !!isDebugMode,
      isWithGlobalTimer: ({ context: { poll } }) => {
        return !!poll.hasTimer;
      },
      isShouldRestoreState: ({ context: { poll } }) => {
        // проверка useSinglePass в первую очередь для обратной совместимости со старыми опросами
        return !!poll.restoreState || !!poll.useSinglePass;
      },
      isPassedPoll: ({ context: { state, poll } }) => {
        if (!state.finishedAt) return false;
        if (poll.singlePassExpiry) return new Date().getTime() - state.finishedAt <= poll.singlePassExpiry;
        return true;
      },
      isWrongConfiguration: ({ context: { poll } }) => {
        const isTimerInvalid = poll.hasTimer && poll.timerDuration === 0;

        const isRestrictedTypePresent =
          location.origin === 'https://media.tinkoff.ru' &&
          poll.questions.some(question => ['POLL_AUTH', 'POLL_FILE'].includes(question.data.type));

        return isTimerInvalid || isRestrictedTypePresent;
      },
      isNotServerPass: ({ context: { poll } }) => {
        return !poll.useServerPass;
      },
      isServerPass: ({ context: { poll } }) => {
        return !!poll.useServerPass;
      },
      isCompletedPoll: ({ context: { poll } }) => {
        if (poll.isCompleted) return true;
        if (poll.isCompletedAtTime) {
          const currentTime = new Date().getTime();
          const completedTime = new Date(poll.isCompletedAtTime).getTime();

          return currentTime >= completedTime;
        }
        return false;
      },
      isCanGoBack: ({ context: { state, poll } }) => {
        const goBackIsEnabled = !!poll.canGoBack;
        const hasPreviousAnsweredQuestion = state.answersStack.some(
          answerState =>
            answerState !== last(state.answersStack) && (answerState.isAnswered || answerState.isWithoutAnswers),
        );
        const previousQuestionIsPollAuth =
          findLastStoredQuestionSafe(state.answersStack, poll.questions)?.data.type === 'POLL_AUTH';
        const isFinishedPoll = state.trackingStack.some(tracking => tracking.type === 'finish');

        return goBackIsEnabled && hasPreviousAnsweredQuestion && !previousQuestionIsPollAuth && !isFinishedPoll;
      },
      isUnauthorizedErrorWithAuthStep: ({ context: { poll }, event }) => {
        if (event.type === 'xstate.error.actor.nextCalculator') {
          return poll.questions.some(q => q.data.type === 'POLL_AUTH') && event.error.name === 'UnauthorizedError';
        }
        return false;
      },
      isForbiddenErrorWithAuthStep: ({ context: { poll }, event }) => {
        if (event.type === 'xstate.error.actor.nextCalculator') {
          return poll.questions.some(q => q.data.type === 'POLL_AUTH') && event.error.name === 'ForbiddenError';
        }
        return false;
      },
      isAllQuestionsSkippable,
      isAllQuestionsSkippableAndAutoCommit: a => isAllQuestionsSkippable(a) && isAutoCommitable(a),
      isAllQuestionsSkippableAndPoll: a => {
        return isAllQuestionsSkippable(a) && a.context.poll.template === 'poll';
      },
      isAllQuestionsUnanswerable: ({ context: { state, poll } }) =>
        selectRelevantQuestions({ state, poll }).every(q => findLastInStack(state.answersStack, q.id).isWithoutAnswers),
      isSomeQuestionsChanged: ({ context: { state, poll } }) =>
        selectRelevantQuestions({ state, poll })
          .filter(q => !findLastInStack(state.answersStack, q.id).isWithoutAnswers)
          .some(q => findLastInStack(state.answersStack, q.id).lastTouchedBy !== null),
      isAllQuestionsUnchanged: ({ context: { state, poll } }) =>
        !selectRelevantQuestions({ state, poll })
          .filter(q => !findLastInStack(state.answersStack, q.id).isWithoutAnswers)
          .some(q => findLastInStack(state.answersStack, q.id).lastTouchedBy !== null),
      isWithWelcome: ({ context: { poll } }) => {
        return !!poll.welcome && poll.welcome.length > 0;
      },
      isStrictTimer: ({ context: { poll, state } }) => {
        if (poll.hasTimer) {
          return !!poll.timerStrict;
        }
        const screenTimerInfo = getScreenTimerInfo({ poll, state });

        return !!screenTimerInfo?.timerStrict;
      },
      isReinit: ({ context: { state } }) => {
        return !!state.startedAt;
      },
      isAutoCommitable,
      isInvalidAnswerOrUnanswerable: ({ context: { state, poll } }) => {
        return isInvalidAnswer({ state, poll, intl }) || isInvalidUnanswerable({ state, poll, intl });
      },
      isInvalidAnswerChange: ({ context: { state, poll } }) =>
        selectRelevantQuestions({ state, poll }).some(
          (question, _, relevantQuestions) =>
            !!validateAnswersOnChange(question, relevantQuestions, state.answersStack, intl),
        ),
      isWithImageFeedback: ({ context: { state, poll } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        return hasImageFeedback({ question });
      },
      hasFeedback: ({ context: { state, poll } }) => {
        const group = state.groupId ? selectGroup(poll, state.groupId) : null;

        if (group?.data.autoNext) return false;

        const questions = selectRelevantQuestions({ state, poll });

        return questions.some(question => {
          const answerState = findLastInStack(state.answersStack, question.id);
          return hasFeedback({ question, answerState, template: poll.template, sendError });
        });
      },
      isReadOnlyLastScreen: ({ context: { state, poll } }) => !!state.readOnly && !!next(poll, state).finalId,
      isFinalScreen: ({ context: { state } }) => !!state.finalId,
      isInstantAutoClose: ({ context: { state, poll } }) => {
        const finalId = state.readOnly ? next(poll, state).finalId : state.finalId;
        return isFinalWithZeroAutoClose(poll, finalId);
      },
      hasAutoClose: ({ context: { state, poll } }) => {
        const finalId = state.readOnly ? next(poll, state).finalId : state.finalId;
        const final = poll.finals.find(final => final.id === finalId);
        return !!final?.data?.hasAutoClose;
      },
      isCloseable: ({ context: { poll } }) => {
        return ('closeable' in poll && !!poll.closeable) || poll.template === 'dialog_webview';
      },
      isValidUnanswerable: ({ context: { poll, state } }) => {
        return selectRelevantQuestions({ state, poll }).every(
          q => !findLastInStack(state.answersStack, q.id).errorMessage,
        );
      },
      hasLoadedExternalLogicRules: ({ context: { state } }) => {
        return !!Object.keys(state.externalLogicRules ?? {}).length;
      },
    },
  }).createMachine({
    /** @xstate-layout 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 */
    context: {
      poll: originalPoll,
      user: undefined,
      state: { ...defaultState, externalLogicRules },
      initServerPassPollError: undefined,
      nextCalculatorError: undefined,
      trackingError: undefined,
      retryCount: 0,
      hasStarted: false,
      trackingDependentActionList: [],
    },
    initial: '$init',
    on: {
      close: {
        target: '.$closing',
        guard: 'isCloseable',
        reenter: true,
      },
      trackStatusUpdate: { actions: 'doTrackStatusUpdate' },
      invokeDeeplink: { actions: 'doInvokeDeeplink' },
      linkClick: { actions: 'doTrackLinkClick' },
    },
    states: {
      $init: {
        always: [
          {
            guard: 'isWithAuthStep',
            target: 'fetchingAuthState',
          },
          {
            target: '$loadedAuthState',
            actions: 'doSetEmptyAuthState',
          },
        ],
      },

      fetchingAuthState: {
        initial: 'loading',
        states: {
          loading: {
            invoke: {
              src: 'authStateLoader',
              id: 'authStateLoader',
              onDone: {
                target: '#(machine).$loadedAuthState',
                actions: 'doSetAuthState',
              },
              input,
              onError: [
                {
                  guard: 'isDebugMode',
                  actions: 'doLogStorageError',
                  target: 'error',
                },
                {
                  target: '#(machine).$loadedAuthState',
                  actions: ['doLogStorageError', 'doSetEmptyAuthState'],
                },
              ],
            },
          },
          retrying: {
            invoke: {
              src: 'authStateLoader',
              id: 'authStateLoader',
              input,
              onDone: {
                target: '#(machine).$loadedAuthState',
                actions: 'doSetAuthState',
              },
              onError: { target: 'error', actions: 'doLogStorageError' },
            },
          },
          error: {
            on: {
              retryLoadingAuthState: {
                target: 'retrying',
              },
            },
          },
        },
      },

      $loadedAuthState: {
        always: [
          {
            guard: 'isWrongConfiguration',
            target: 'wrongConfiguration',
          },
          {
            guard: 'isCompletedPoll',
            target: 'completedPoll',
          },
          {
            target: 'restoringBeforeAuthState',
          },
        ],
      },

      restoringFromPollsSiteStorage: {
        description: 'Восстановление состояния из хранилища',
        invoke: {
          id: 'persistentStateRestorer',
          src: 'persistentStateRestorer',
          input,
          onDone: { target: '$restoredState', actions: 'doUseFetchedState' },
          onError: { target: '$restoredState', actions: 'doLogStorageError' },
        },
      },

      initServerPassPoll: {
        description: 'Получение структуры опроса с polls site',
        invoke: {
          id: 'initServerPassPoll',
          src: 'initServerPassPoll',
          input,
          onDone: {
            target: '$initializedPoll',
            actions: ['doInitializeServerPassPoll', 'doClearinitServerPassPollErrors'],
          },
          onError: {
            target: 'initServerPassPollError',
            actions: ['doNotifyNetworkError', 'doinitServerPassPollErrorAndIncrementRetryCount'],
          },
        },
      },

      initServerPassPollError: {
        on: {
          retryInitServerPassPoll: {
            description: 'Повтор получения структуры опроса с polls site',
            target: 'initServerPassPoll',
          },
        },
      },

      restoringBeforeAuthState: {
        description: 'Восстановление после редиректа с сервиса авторизации',
        invoke: {
          id: 'beforeAuthStateRestorer',
          src: 'beforeAuthStateRestorer',
          input,
          onDone: { target: '$restoredBeforeAuth', actions: 'doUseFetchedState' },
          onError: { target: '$restoredBeforeAuth', actions: 'doLogStorageError' },
        },
      },

      wrongConfiguration: {
        description: 'Если опрос с шагом авторизации или загрузкой файлов открыт не на polls site',
        type: 'final',
        entry: 'doNotifyWrongConfiguration',
      },

      completedPoll: {
        description: 'Состояние завершённого опроса (из админки или по лимиту времени)',
        entry: 'doNotifyCompleted',
      },

      passedPoll: {
        description: 'Состояние пройденного пользователем опроса',
        entry: 'doNotifyPassed',
      },

      $initializedPoll: {
        always: [
          {
            target: 'transforming',
            guard: 'hasLoadedExternalLogicRules',
          },
          { target: 'loadingExternalLogicRules' },
        ],
      },

      loadingExternalLogicRules: {
        invoke: {
          src: 'externalLogicRulesLoader',
          id: 'externalLogicRulesLoader',
          input,
          onDone: { target: 'transforming', actions: 'doSetExternalLogicRules' },
          onError: { target: 'transforming', actions: 'doLogExternalLogicRulesError' },
        },
      },

      transforming: {
        always: [
          {
            target: '$transformed',
            guard: 'isNotServerPass',
            actions: 'doPreStartTransformPoll',
          },
          { target: '$transformed' },
        ],
      },

      $transformed: {
        always: [
          {
            target: 'savingStateToStorage',
            guard: 'isShouldRestoreState',
          },
          { target: '$loaded' },
        ],
      },

      savingStateToStorage: {
        description:
          'Сохранение состояния прохождения опроса для обеспечения консистентности внешних логических условия, рандомизации и т.д.',
        invoke: {
          src: 'immediatelyUpdateStorageState',
          id: 'immediatelyUpdateStorageState',
          input,
          onDone: { target: '$loaded' },
          onError: { target: '$loaded' },
        },
      },

      $loaded: {
        entry: 'doTrackLoad',
        always: [
          {
            guard: 'isPassedPoll',
            target: 'passedPoll',
          },
          {
            target: '$ready',
          },
        ],
      },

      $ready: {
        entry: 'doNotifyReady',
        always: [
          {
            guard: 'isReinit',
            target: 'questions',
            actions: 'doSetTrackingUserIfHadAnsweredAuthSuccessfully',
          },
          {
            guard: 'isWithWelcome',
            target: 'welcome',
            actions: 'doSetWelcome',
          },
          {
            guard: 'isWithGlobalTimer',
            target: 'globalTimerWelcome',
            actions: 'doInit',
          },
          {
            target: 'questions',
            actions: 'doInit',
          },
        ],
      },

      welcome: {
        on: {
          welcomeClick: {
            target: 'questions',
            actions: ['doWelcomeClick', 'doTrackWelcomeClick'],
          },
        },
      },

      globalTimerWelcome: {
        on: {
          globalTimerStart: {
            target: 'questions',
            actions: 'doStartGlobalTimer',
          },
        },
      },

      questions: {
        initial: 'screen',

        states: {
          $switching: {
            entry: 'doTrackImageVisibility',
            always: [{ target: 'screen', actions: 'doUseNextState' }],
          },

          $switchingBack: {
            always: {
              target: 'screen.changed.submittable.unlocked',
              actions: 'doBack',
            },
          },

          screen: {
            initial: 'tracked',

            on: {
              questionFocus: {
                actions: 'doTrackStart',
              },

              mediaProgressChange: {
                actions: ['doTrackStart', 'doMediaProgressChange'],
              },

              mediaProgress: {
                actions: 'doTrackMediaProgress',
              },
            },

            states: {
              changed: {
                initial: '$validating',
                on: {
                  back: { target: '#(machine).questions.$switchingBack', guard: 'isCanGoBack' },
                  skip: { target: '$skipping', guard: 'isAllQuestionsSkippableAndAutoCommit' },
                },
                always: {
                  guard: 'isAllQuestionsUnchanged',
                  target: 'tracked',
                },
                states: {
                  $validating: {
                    always: [{ guard: 'isInvalidAnswerChange', target: 'invalid' }, 'submittable'],
                  },

                  submittable: {
                    on: {
                      answer: '$submitted',
                    },

                    initial: 'unlocked',

                    states: {
                      locked: {},

                      unlocked: {
                        after: {
                          REMAINED_TIMER: [
                            {
                              guard: 'isStrictTimer',
                              target: 'locked',
                              actions: 'doMarkTimeExpired',
                            },
                            {
                              actions: 'doMarkTimeExpired',
                            },
                          ],
                        },

                        on: {
                          change: { target: '#(machine).questions.screen.changed.$validating', actions: 'doChange' },

                          othersChange: {
                            target: '#(machine).questions.screen.changed.$validating',
                            actions: 'doOthersChange',
                          },

                          fileUpload: {
                            target: '#(machine).questions.screen.changed.$validating',
                            actions: ['doTrackFileUpload', 'doFileUpload'],
                          },

                          skip: {
                            target: '#(machine).questions.screen.$skipping',
                            guard: 'isAllQuestionsSkippableAndPoll',
                          },
                        },

                        always: {
                          guard: 'isAutoCommitable',
                          actions: 'doMarkAutoCommited',
                          target: '#(machine).questions.screen.changed.$submitted',
                        },
                      },
                    },
                  },

                  $submitted: {
                    always: [
                      {
                        guard: 'isInvalidAnswerOrUnanswerable',
                        actions: ['doMarkInvalidAnswers', 'doMarkInvalidUnanswerable'],
                        target: 'invalid',
                      },

                      {
                        actions: 'doMarkAnswered',
                        target: '#(machine).questions.screen.tracking',
                      },
                    ],
                  },

                  invalid: {
                    initial: 'unlocked',

                    states: {
                      unlocked: {
                        on: {
                          change: { target: '#(machine).questions.screen.changed.$validating', actions: 'doChange' },

                          othersChange: {
                            target: '#(machine).questions.screen.changed.$validating',
                            actions: 'doOthersChange',
                          },

                          fileUpload: {
                            target: '#(machine).questions.screen.changed.$validating',
                            actions: ['doTrackFileUpload', 'doFileUpload'],
                          },

                          tryAnswer: '#(machine).questions.screen.changed.$submitted',
                        },
                        after: {
                          REMAINED_TIMER: [
                            {
                              guard: 'isStrictTimer',
                              target: 'locked',
                              actions: 'doMarkTimeExpired',
                            },
                            {
                              actions: 'doMarkTimeExpired',
                            },
                          ],
                        },
                      },

                      locked: {
                        on: {
                          skip: { target: '#(machine).questions.screen.$skipping', actions: 'doTrackStart' },
                        },
                      },
                    },
                  },
                },
              },

              tracked: {
                on: {
                  change: { target: 'changed', actions: ['doTrackStart', 'doChange'] },
                  othersChange: { target: 'changed', actions: ['doTrackStart', 'doOthersChange'] },
                  fileUpload: { target: 'changed', actions: ['doTrackFileUpload', 'doFileUpload'] },

                  back: {
                    target: '#(machine).questions.$switchingBack',
                    guard: 'isCanGoBack',
                  },

                  tryAnswer: { target: 'changed.$submitted', actions: ['doTrackStart'] },

                  skip: { target: '$skipping', actions: 'doTrackStart', guard: 'isAllQuestionsSkippable' },
                },
                after: {
                  REMAINED_TIMER: [
                    {
                      guard: 'isStrictTimer',
                      target: 'expired',
                      actions: 'doMarkTimeExpired',
                    },
                    {
                      actions: 'doMarkTimeExpired',
                    },
                  ],
                },
                always: [
                  { guard: 'isOnAuthStep', target: 'auth' },
                  {
                    guard: 'isSomeQuestionsChanged',
                    target: 'changed',
                  },
                  {
                    guard: 'isAllQuestionsUnanswerable',
                    target: 'unanswerable',
                  },
                ],
              },

              trackingError: {
                on: {
                  retryPixelSend: {
                    description: 'Событие повтора отправки пикселей',
                    target: '#(machine).questions.screen.tracking.sendingPixels',
                  },
                },
              },

              tracking: {
                initial: 'calculatingNext',
                states: {
                  calculatingNext: {
                    invoke: {
                      id: 'nextCalculator',
                      src: 'nextCalculator',
                      input,
                      onDone: {
                        target: 'sendingPixels',
                        actions: ['doSetNextState', 'doClearNextCalculatorErrors'],
                      },
                      onError: [
                        {
                          guard: 'isUnauthorizedErrorWithAuthStep',
                          target: 'nextCalculatorUnauthorizedError',
                          actions: ['doNotifyNetworkError'],
                        },
                        {
                          guard: 'isForbiddenErrorWithAuthStep',
                          target: '#(machine).forbiddenError',
                          actions: ['doNotifyNetworkError'],
                        },
                        {
                          target: 'nextCalculatorError',
                          actions: ['doNotifyNetworkError', 'doAddNextCalculatorErrorAndIncrementRetryCount'],
                        },
                      ],
                    },
                  },
                  sendingPixels: {
                    invoke: {
                      id: 'pixelSender',
                      src: 'pixelSender',
                      input,
                      onDone: {
                        target: '#(machine).questions.screen.answered',
                        actions: ['doClearTrackingErrors', 'doSetLastPixelHash', 'doMarkAnswerTrackingStatus'],
                      },
                      onError: {
                        target: '#(machine).questions.screen.trackingError',
                        actions: ['doNotifyNetworkError', 'doAddTrackingErrorAndIncrementRetryCount'],
                      },
                    },
                  },
                  nextCalculatorError: {
                    on: {
                      retryNextCalculator: {
                        description: 'Событие повтора отправки next action',
                        target: '#(machine).questions.screen.tracking.calculatingNext',
                      },
                    },
                  },
                  nextCalculatorUnauthorizedError: {
                    on: {
                      refreshAuthentication: {
                        target: '#(machine).questions.screen.auth.dumpingStateBeforeAuth',
                        actions: 'doClearTokenInStorage',
                      },
                    },
                  },
                },
                entry: ['doTrackImageVisibility'],
              },

              answered: {
                initial: '$checkingFeedback',
                states: {
                  $checkingFeedback: {
                    always: [
                      {
                        guard: 'isWithImageFeedback',
                        target: '#(machine).questions.$switching',
                      },
                      {
                        guard: 'hasFeedback',
                        target: 'feedback',
                      },
                      {
                        target: '#(machine).questions.$switching',
                      },
                    ],
                  },
                  feedback: {
                    on: {
                      next: { target: '#(machine).questions.$switching' },
                      back: { target: '#(machine).questions.$switchingBack', guard: 'isCanGoBack' },
                    },
                  },
                },
                exit: 'doTrackImageVisibility',
              },

              auth: {
                initial: '$init',
                states: {
                  $init: {
                    always: [
                      {
                        guard: 'isReturningFromAuth',
                        target: '$returning',
                      },
                      {
                        guard: 'isAuthenticatedWithSkipAuthenticated',
                        target: '$continuing',
                      },
                      'unanswered',
                    ],
                  },

                  unanswered: {
                    on: {
                      continueAuthenticated: {
                        guard: 'isAuthenticated',
                        actions: ['doTrackStart'],
                        target: '$continuing',
                      },
                      startAuthentication: {
                        guard: 'isNotAuthenticated',
                        actions: ['doTrackStart', 'doChangeToAuthorizedAnswer'],
                        target: 'dumpingStateBeforeAuth',
                      },
                      skipAuthentication: {
                        guard: 'isNotRequiredAuth',
                        actions: ['doTrackStart', 'doChangeToUnauthorizedAnswer'], // TODO: move doTrackStart to exit effect of tracked and make tracked state compound
                        target: '$answering',
                      },
                    },
                  },

                  retrying: {
                    on: {
                      startAuthentication: {
                        actions: ['doChangeToAuthorizedAnswer'],
                        target: 'dumpingStateBeforeAuth',
                      },
                      skipAuthentication: {
                        guard: 'isNotRequiredAuth',
                        actions: ['doChangeToUnauthorizedAnswer'],
                        target: '$answering',
                      },
                    },
                  },

                  dumpingStateBeforeAuth: {
                    invoke: {
                      src: 'beforeAuthStateDumper',
                      id: 'beforeAuthStateDumper',
                      input,
                      onDone: {
                        actions: 'doRedirectToLogin',
                        target: 'redirecting',
                      },
                      onError: {
                        target: 'redirecting',
                        actions: ['doLogStorageError'],
                      },
                    },
                  },

                  redirecting: {
                    type: 'final',
                  },

                  $continuing: {
                    always: {
                      actions: ['doSetTrackingUser', 'doChangeToAuthorizedAnswer'],
                      target: '$answering',
                    },
                  },

                  $answering: {
                    always: {
                      actions: 'doMarkAnswered',
                      target: '#(machine).questions.screen.tracking',
                    },
                  },

                  $returning: {
                    always: [
                      {
                        guard: 'isAuthenticated',
                        actions: ['doSetTrackingUser'],
                        target: '$answering',
                      },
                      {
                        actions: ['doChangeToUnauthorizedAnswer', 'doLogAuthErrorAfterRedirect'],
                        target: 'retrying',
                      },
                    ],
                  },
                },
              },

              unanswerable: {
                initial: 'init',
                states: {
                  init: {
                    on: {
                      next: { target: '$validating', actions: 'doTrackStart' },
                      mediaProgressChange: {
                        target: 'init',
                        actions: ['doTrackStart', 'doMediaProgressChange'],
                      },
                      back: {
                        target: '#(machine).questions.$switchingBack',
                        guard: 'isCanGoBack',
                      },
                    },
                  },

                  $validating: {
                    entry: 'doMarkInvalidUnanswerable',
                    always: [
                      { target: '#(machine).questions.screen.tracking', guard: 'isValidUnanswerable' },
                      { target: 'init' },
                    ],
                  },
                },
              },

              expired: {
                on: {
                  skip: { target: '$skipping', actions: 'doTrackStart' },
                },
              },

              $skipping: {
                entry: 'doMarkSkipped',
                always: {
                  target: 'tracking',
                },
              },
            },

            always: [
              {
                guard: 'isReadOnlyLastScreen',
                target: '#(machine).final',
              },
              {
                guard: 'isFinalScreen',
                target: '#(machine).final',
              },
            ],
          },
        },
      },

      final: {
        initial: '$init',

        states: {
          $init: {
            entry: ['doSetFinishedAt', 'doNotifyDone', 'doNotifyDoneRequests', 'doNotifyOverall'],

            always: [
              {
                guard: 'isInstantAutoClose',
                actions: 'doAutoClose',
                target: '#(machine).instantlyAutoClosed',
              },
              {
                guard: 'hasAutoClose',
                target: 'closingFinished',
              },
              {
                target: 'finished',
              },
            ],
          },

          finished: {},

          closingFinished: {
            after: {
              AUTO_CLOSE_TIMEOUT: {
                target: 'finished',
                actions: 'doAutoClose',
              },
            },
          },
        },

        on: {
          finalClick: {
            actions: 'doTrackFinalClick',
            target: 'final',
          },
        },
      },

      $closing: {
        entry: 'doClose',
        always: 'closed',
      },

      closed: {},

      instantlyAutoClosed: {},

      forbiddenError: {},

      $restoredBeforeAuth: {
        always: [
          {
            guard: 'isShouldRestoreState',
            target: 'restoringFromPollsSiteStorage',
          },
          { target: '$restoredState' },
        ],
      },

      $restoredState: {
        always: [
          {
            guard: 'isServerPass',
            target: 'initServerPassPoll',
          },
          { target: '$initializedPoll' },
        ],
      },
    },
  });
}
/** Входные данные для внешних сервисов (акторов) */
const input = ({ context }: { context: PollMachineContext }) => context;

function isAutoCommitable({ context: { state, poll } }: { context: { state: ICState; poll: Poll } }) {
  const answersMap = getMapFromStack(state.answersStack);
  const group = state.groupId ? selectGroup(poll, state.groupId) : null;

  return selectRelevantQuestions({ state, poll }).every(q => {
    const autoCommit = group?.data.autoCommit ?? ('autoCommit' in q.data && q.data.autoCommit);
    const { lastTouchedBy, isWithoutAnswers } = findLastInStack(state.answersStack, q.id);
    const isTouchedByUser = lastTouchedBy === 'user';
    const hasTextfield = questionHasDisclaimer(q, answersMap[q.id]);
    const isMultiple = getEngine(poll.template).hasTrait(q.data.type, 'MULTIPLE_ANSWERS_TYPE');

    return (autoCommit && isTouchedByUser && !hasTextfield && !isMultiple) || isWithoutAnswers;
  });
}

function isAllQuestionsSkippable({ context: { state, poll } }: { context: { state: ICState; poll: Poll } }) {
  return selectRelevantQuestions({ state, poll })
    .filter(q => !findLastInStack(state.answersStack, q.id).isWithoutAnswers)
    .every(
      q =>
        ('requiresAnswer' in q.data && !q.data.requiresAnswer) ||
        (q.data.type === 'POLL_PROTOTYPE_SCREEN' &&
          (q.data.answers.filter(a => 'area' in a).length === 0 || q.data.isFinal)),
    );
}

const isInvalidUnanswerable = ({ state, poll, intl }: { state: ICState; poll: Poll; intl: InterpreterIntl }) => {
  const unanswerableQuestions = selectRelevantQuestions({ state, poll })
    .map(question => ({
      question,
      answerState: findLastInStack(state.answersStack, question.id),
    }))
    .filter(({ answerState }) => findLastInStack(state.answersStack, answerState.id).isWithoutAnswers)
    .map(({ question }) => ({
      question,
      answerState: findLastInStack(state.answersStack, question.id),
    }));

  return unanswerableQuestions.some(({ question, answerState }) => !!validateUnanswerable(question, answerState, intl));
};

const isInvalidAnswer = ({ state, poll, intl }: { state: ICState; poll: Poll; intl: InterpreterIntl }) =>
  selectRelevantQuestions({ state, poll }).some(
    (question, _, relevantQuestions) => !!validateAnswers(question, relevantQuestions, state.answersStack, intl),
  );

function getTimerCreatedAt(state: ICState, poll: Poll) {
  if (state.groupId) {
    const questions = selectDisplayedOrAllGroupQuestions(poll, state.groupId, state.answersStack);

    for (const question of questions) {
      const found = findLastInStackSafe(state.answersStack, question.id);
      if (found && found.createdAt) return found.createdAt;
    }
  } else if (state.questionId) {
    const found = findLastInStackSafe(state.answersStack, state.questionId);
    if (found && found.createdAt) return found.createdAt;
  }

  return undefined;
}
function getScreenTimerInfo({ poll, state: { groupId, questionId } }: { poll: Poll; state: ICState }) {
  if (groupId) {
    const group = selectGroup(poll, groupId);
    if (group.data.hasTimer) {
      return {
        timerDuration: group.data.timerDuration ?? 0,
        timerStrict: Boolean(group.data.timerStrict),
        timerExpiredText: group.data.timerExpiredText,
      };
    }
    /** Таймер на вопрос */
  } else if (questionId) {
    const question: {
      data: Wide<{ hasTimer?: boolean; timerExpiredText?: string; timerStrict?: boolean; timerDuration?: number }>;
    } = selectQuestion(poll, questionId);

    if (question.data.hasTimer) {
      return {
        timerDuration: question.data.timerDuration ?? 0,
        timerStrict: Boolean(question.data.timerStrict),
        timerExpiredText: question.data.timerExpiredText,
      };
    }
  }
  return undefined;
}

/** Объявляет тип, который принимает объекты без указанных свойств (но и без конфликтов)
 * ```
 * const ex0:      { a: number }  = { b: "str" }; // error!
 * const ex1: Wide<{ a: number }> = { b: "str" }; // ok
 * const ex2: Wide<{ a: number }> = { a: 123, b: "str" }; // ok
 * const ex3: Wide<{ a: number }> = { a: "str" }; // error!
 * ```
 */
declare type Wide<T> = T & object;

function canClosePoll({
  state,
  poll,
  waitOnlySystemPixels,
}: {
  state: ICState;
  poll: Poll;
  waitOnlySystemPixels?: boolean;
}) {
  return !shouldWaitForPixels({ state, poll }) || isAllPixelSent({ state, onlySystem: waitOnlySystemPixels });
}

function shouldWaitForPixels({ state, poll }: { state: ICState; poll: Poll }) {
  const hasPixels = !!poll.pixels.length;
  const isFinished = !!state.finishedAt;
  return hasPixels && isFinished;
}

function isAllPixelSent({ state, onlySystem = false }: { state: ICState; onlySystem?: boolean }) {
  return (
    !!state.trackingStack.length &&
    state.trackingStack.every(trackingState =>
      trackingState.tracking.every(track => {
        if (track.status !== 'pending') return true;
        return onlySystem && !track.isSystem;
      }),
    )
  );
}
